import React, { lazy, Suspense } from 'react';
import 'aws-amplify/auth/enable-oauth-listener';
import '@business/assets/css/variables.css';
import '@business/assets/css/theme.css';
import '@business/assets/css/global.css';
import { Amplify } from 'aws-amplify';
import { ComponentProvider } from '@business/workflows/ComponentHigherOrder';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ToastContainer } from 'react-toastify';
import { RouterInfo } from '@business/entities/base';
import MyProfile from '@business/workflows/ComponentViews/MyProfile/MyProfile';
import { AuthLayout } from './layouts/AuthLayout';
import amplifyconfig from './amplifyconfiguration.json';

const SignUp = lazy(() => import('@pages/signup'));
const SignIn = lazy(() => import('@pages/sign-in'));
const ForgotPassword = lazy(
  () =>
    import('@business/workflows/ComponentViews/ForgotPassword/ForgotPassword')
);
const ResetPassword = lazy(
  () => import('@business/workflows/ComponentViews/ResetPassword/ResetPassword')
);

const TwoFactorAuthentication = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/TwoFactorAuthentication/TwoFactorAuthentication'
    )
);

const Subscription = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Subscription/SubscriptionPlanList'
    )
);
const SelectedSubscriptionPlan = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Subscription/SelectedSubscriptionPlan'
    )
);
const PaymentSuccess = lazy(
  () => import('@business/workflows/ComponentViews/Subscription/PaymentSuccess')
);

const EditProfile = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/EditProfile')
);

const SubscriptionSettings = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/SubscriptionsSettings'
    )
);
const PaymentSettings = lazy(
  () => import('@business/workflows/ComponentViews/AccountSettings/Payments')
);
const SecurityPrivacySettings = lazy(
  () =>
    import('@business/workflows/ComponentViews/AccountSettings/SecurityPrivacy')
);
const NotificationSettings = lazy(
  () =>
    import('@business/workflows/ComponentViews/AccountSettings/Notification')
);
const HelpSettings = lazy(
  () => import('@business/workflows/ComponentViews/AccountSettings/Help')
);
const TakeAPicture = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/TakeAPicture')
);
const ManageSubscriptions = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/SubscriptionsSettings/ManageSubscriptions'
    )
);
const StorageSettings = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/StorageContributorSettings/StorageContributorSettings'
    )
);
const Photos = lazy(
  () => import('@business/workflows/ComponentViews/Photos/Photos')
);
const PhotoCamera = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/TakeAPicture')
);
const MediaPostWrapper = lazy(
  () => import('@business/workflows/ComponentViews/Photos/MediaPostWrapper')
);

const Folder = lazy(
  () => import('@business/workflows/ComponentViews/Folder/Folder')
);
const FolderPhotos = lazy(
  () => import('@business/workflows/ComponentViews/Folder/FolderPhotos')
);
const People = lazy(
  () => import('@business/workflows/ComponentViews/People/People')
);
const Favorites = lazy(
  () => import('@business/workflows/ComponentViews/Favorites/Favorites')
);
const FamilyTree = lazy(
  () => import('@business/workflows/ComponentViews/FamilyTree/FamilyTree')
);
const Memories = lazy(
  () => import('@business/workflows/ComponentViews/Memories/Memories')
);
const Recipes = lazy(
  () => import('@business/workflows/ComponentViews/Recipies/Recipies')
);
const Sharing = lazy(
  () => import('@business/workflows/ComponentViews/Sharing/Sharing')
);
const Storage = lazy(
  () => import('@business/workflows/ComponentViews/Storage/Storage')
);
const Trash = lazy(
  () => import('@business/workflows/ComponentViews/Trash/Trash')
);

const App = () => {
  Amplify.configure(amplifyconfig);

  const fallbackRender = (_error: any) => {
    return (
      <>
        <div />
      </>
    );
  };

  const convertComponent = (routerInfo: any) => {
    switch (routerInfo?.layout) {
      case 'error':
        return <>{routerInfo?.element}</>;
      case 'dashboard':
        return <>{routerInfo?.element}</>;
      case 'auth':
        return (
          <AuthLayout isSignUp={routerInfo?.path === '/signup'}>
            {routerInfo?.element}
          </AuthLayout>
        );
      case 'public':
        return <>{routerInfo?.element}</>;
      default:
        return <>{routerInfo?.element}</>;
    }
  };

  const pageRouters: RouterInfo[] = [
    {
      path: '/',
      element: <SignIn />,
      layout: 'dashboard'
    },
    {
      path: '/signup',
      element: <SignUp />,
      layout: 'auth'
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
      layout: 'auth'
    },
    {
      path: '/reset-password/:token',
      element: <ResetPassword />,
      layout: 'auth'
    },
    {
      path: '/two-factor-authentication',
      element: <TwoFactorAuthentication />,
      layout: 'auth'
    },
    {
      path: '/subscription',
      element: <Subscription />,
      layout: 'dashboard'
    },
    {
      path: '/selected-subscription',
      element: <SelectedSubscriptionPlan />,
      layout: 'dashboard'
    },
    {
      path: '/payment-success',
      element: <PaymentSuccess />,
      layout: 'dashboard'
    },
    {
      path: '/my-profile/edit',
      element: <EditProfile />,
      layout: ''
    },
    {
      path: '/settings/subscriptions',
      element: <SubscriptionSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/payments',
      element: <PaymentSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/security-privacy',
      element: <SecurityPrivacySettings />,
      layout: 'profile'
    },
    {
      path: '/settings/notification',
      element: <NotificationSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/help',
      element: <HelpSettings />,
      layout: 'profile'
    },
    {
      path: '/my-profile',
      element: <MyProfile />,
      layout: 'profile'
    },
    {
      path: '/my-profile/camera',
      element: <TakeAPicture />,
      layout: 'profile'
    },
    {
      path: '/settings/subscriptions/manage',
      element: <ManageSubscriptions />,
      layout: 'profile'
    },
    {
      path: '/settings/storage',
      element: <StorageSettings />,
      layout: 'profile'
    },
    {
      path: '/photos',
      element: <Photos />,
      layout: 'profile'
    },
    {
      path: '/photos/media/:assetId',
      element: <MediaPostWrapper />,
      layout: 'profile'
    },
    {
      path: '/photos/camera',
      element: <PhotoCamera />,
      layout: 'profile'
    },
    {
      path: '/photo/folders',
      element: <Folder />,
      layout: 'profile'
    },
    {
      path: '/photo/folders/:folderName',
      element: <FolderPhotos />,
      layout: 'profile'
    },
    {
      path: '/people',
      element: <People />,
      layout: 'profile'
    },
    {
      path: '/favorites',
      element: <Favorites />,
      layout: 'profile'
    },
    {
      path: '/family-tree',
      element: <FamilyTree />,
      layout: 'profile'
    },
    {
      path: '/memories',
      element: <Memories />,
      layout: 'profile'
    },
    {
      path: '/recipes',
      element: <Recipes />,
      layout: 'profile'
    },
    {
      path: '/sharing',
      element: <Sharing />,
      layout: 'profile'
    },
    {
      path: '/storage',
      element: <Storage />,
      layout: 'profile'
    },
    {
      path: '/trash',
      element: <Trash />,
      layout: 'profile'
    }
  ];

  return (
    <ComponentProvider>
      <BrowserRouter>
        <ErrorBoundary
          fallbackRender={fallbackRender}
          onReset={() => (location.href = '/')}>
          <Suspense fallback={<></>}>
            <Routes>
              {pageRouters.map(routeObj => (
                <Route
                  path={routeObj?.path}
                  element={convertComponent(routeObj)}
                  key={routeObj?.path}
                />
              ))}
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ComponentProvider>
  );
};

export default App;
